import { Injectable } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { PaginationService } from "../../shared/services/pagination.service";
import { PaginatedEvent } from "@models/pagination_event";
import { Observable, of as observableOf } from "rxjs";
import queryString from "query-string";
import { catchError, map } from "rxjs/operators";
import { ErrorClass } from "@models";
import { Helper, parseApiResponseArray2 } from "@helpers/functions";
import { AssetScroll } from "@models/asset_scroll";
import { CoreStoreFacade } from "@modules/+new/core/core-store/domain/+state/core-store.facade";
import { Dictionary } from "@helpers/types";
import { PriceChange } from "@modules/+new/core/tradables/domain/entities/price-change";
import { API_LEVERAGED_TOKEN_TYPE, BEST_SYMBOL, EXCLUDED_ASSET_SYMBOLS } from "@helpers/constants";

// model for new endpoint
// (must be class to satisfy ApicallService)
export class PricesAsset {
	group_id?: number;
	name: string;
	pid: string;
	price_data?: PricesPriceData;
	symbol: string;
	type_id?: number;
	group?: string;
}

export class PricesPriceData {
	change_amount: Dictionary<number>;
	change_percent: PriceChange;
	circulating_supply: number;
	market_cap: number;
	monthly_trading_volume: number;
	monthly_volatility: number;
	price: number;
	updated_at: string;
}

@Injectable()
export class PricesScrollService extends PaginationService<PricesAsset> {
	public constructor(
		public apiCallService: ApicallService,
		protected readonly coreStoreFacade: CoreStoreFacade
	) {
		// this endpoint and version are for "old/super" getApiData
		super(coreStoreFacade, apiCallService, "assets", "v3", PricesAsset);
	}

	override getApiData(e: PaginatedEvent): Observable<PricesAsset[]> {
		// new API uses different syntax: `sort_by` was changed to `sort`
		if (e.getSortBy()) {
			e.setSort(e.getSortBy());
			e.setSortBy("");
		}

		const queryParams = queryString.stringify(e, {
			arrayFormat: "bracket",
			skipEmptyString: true,
			skipNull: true
		});
		queryParams.toLowerCase();

		const apiEndpoint = `prices/assets?${queryParams}`;
		const option = new ApicallOption();
		option.apiVersion = "v1";
		this.coreStoreFacade.updateOfflineState(false);

		return this.apiCallService.get(apiEndpoint, option).pipe(
			map((res) => {
				//leveraged_token group and BEST tokens should not be displayed in search
				const filteredResults = res.filter(
					(list: { group: string; symbol: string }) =>
						list.group !== API_LEVERAGED_TOKEN_TYPE &&
						list.symbol !== BEST_SYMBOL.toUpperCase() &&
						!EXCLUDED_ASSET_SYMBOLS.includes(list.symbol)
				);

				return parseApiResponseArray2<PricesAsset>(filteredResults);
			}),
			catchError((error: Error) => {
				this.fetchError.next(true);
				if (error instanceof ErrorClass) {
					return observableOf(null);
				}
				Helper.handleError(error);
				return observableOf([]);
			})
		);
	}

	// inheritance stuff
	public convertPaginationList(list: AssetScroll[]): PricesAsset[] {
		if (!list) {
			return [];
		}

		return list;
	}

	// Hard reset in case of api-error during data-fetching
	public refetchList(filters: string[] = [""]): void {
		this.resetList();
		this.resetError();
		this.filterUpdate(filters, "");
	}
}
